/**
 * Strongly-typed API client generated against Section 7 OpenAPI contract (§7 & SAAS-311).
 * Eliminates ad-hoc route strings across frontend views.
 */

import type {
  AuthCallbackResponse,
  AuthLoginResponse,
  BranchCreateRequest,
  BranchListResponse,
  BranchResponse,
  ConversionCreateRequest,
  ConversionListResponse,
  ConversionResponse,
  CsrfResponse,
  DocumentCreateRequest,
  DocumentListResponse,
  DocumentResponse,
  DocumentUpdateRequest,
  ErrorEnvelope,
  HeadUpdateRequest,
  JobResponse,
  NotebookPageCreateRequest as PageCreateRequest,
  NotebookPageCreateResponse as PageCreateResponse,
  NotebookPageListResponse as PageListResponse,
  NotebookResponse,
  NotebookResumePutRequest as ResumePutRequest,
  NotebookResumeResponse as ResumeResponse,
  RevisionCreateRequest,
  RevisionListResponse,
  RevisionResponse,
  UploadCompleteResponse,
  UploadCreateRequest,
  UploadResponse,
  UserProfileResponse,
} from "./types.ts";

export class ApiClientError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly requestId: string;
  public readonly retryable: boolean;
  public readonly details: Record<string, unknown>;

  constructor(
    code: string,
    message: string,
    status: number,
    requestId: string,
    retryable: boolean = false,
    details: Record<string, unknown> = {}
  ) {
    super(message);
    this.name = "ApiClientError";
    this.code = code;
    this.status = status;
    this.requestId = requestId;
    this.retryable = retryable;
    this.details = details;
  }
}

function generateRequestId(): string {
  return "req_" + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

export class Paper2InkApiClient {
  private readonly baseUrl: string;
  private tenantId?: string;
  private csrfToken?: string;

  constructor(options: { baseUrl?: string; tenantId?: string; csrfToken?: string } = {}) {
    this.baseUrl = (options.baseUrl || "/api/v1").replace(/\/+$/, "");
    this.tenantId = options.tenantId;
    this.csrfToken = options.csrfToken;
  }

  public setTenantId(tenantId: string | undefined): void {
    this.tenantId = tenantId;
  }

  public setCsrfToken(token: string | undefined): void {
    this.csrfToken = token;
  }

  private async request<T>(
    path: string,
    options: {
      method?: string;
      headers?: Record<string, string>;
      body?: unknown;
      ifMatch?: string;
      idempotencyKey?: string;
    } = {}
  ): Promise<{ data: T; headers: Headers; etag?: string }> {
    const url = `${this.baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
    const headers: Record<string, string> = {
      Accept: "application/json",
      "X-Request-Id": generateRequestId(),
      ...(options.headers || {}),
    };

    if (this.tenantId) {
      headers["X-Tenant-Id"] = this.tenantId;
    }

    if (this.csrfToken && ["POST", "PUT", "PATCH", "DELETE"].includes(options.method?.toUpperCase() || "GET")) {
      headers["X-CSRF-Token"] = this.csrfToken;
    }

    if (options.ifMatch) {
      headers["If-Match"] = options.ifMatch;
    }

    if (options.idempotencyKey) {
      headers["Idempotency-Key"] = options.idempotencyKey;
    }

    let bodyPayload: BodyInit | undefined;
    if (options.body !== undefined) {
      if (
        typeof Blob !== "undefined" && options.body instanceof Blob ||
        typeof ArrayBuffer !== "undefined" && options.body instanceof ArrayBuffer ||
        typeof Uint8Array !== "undefined" && options.body instanceof Uint8Array
      ) {
        bodyPayload = options.body as BodyInit;
      } else {
        headers["Content-Type"] = "application/json";
        bodyPayload = JSON.stringify(options.body);
      }
    }

    const response = await fetch(url, {
      method: options.method || "GET",
      headers,
      body: bodyPayload,
      credentials: "same-origin",
    });

    const reqId = response.headers.get("X-Request-Id") || headers["X-Request-Id"];
    const etag = response.headers.get("ETag") || undefined;

    if (!response.ok) {
      let errEnvelope: ErrorEnvelope | null = null;
      try {
        errEnvelope = (await response.json()) as ErrorEnvelope;
      } catch {
        // Not JSON
      }

      if (errEnvelope && errEnvelope.error) {
        const err = errEnvelope.error;
        throw new ApiClientError(
          err.code,
          err.message,
          response.status,
          err.request_id || reqId,
          err.retryable,
          err.details
        );
      }

      throw new ApiClientError(
        `http_${response.status}`,
        `HTTP request failed with status ${response.status}`,
        response.status,
        reqId,
        response.status >= 500
      );
    }

    const json = (await response.json()) as T;
    return { data: json, headers: response.headers, etag };
  }

  // -------------------------------------------------------------------------
  // Auth Namespaces (§7)
  // -------------------------------------------------------------------------

  public readonly auth = {
    login: async (returnPath: string = "/library"): Promise<AuthLoginResponse> => {
      const q = new URLSearchParams({ return_path: returnPath });
      const res = await this.request<AuthLoginResponse>(`/auth/login?${q.toString()}`);
      return res.data;
    },

    callback: async (code: string, state: string): Promise<AuthCallbackResponse> => {
      const q = new URLSearchParams({ code, state });
      const res = await this.request<AuthCallbackResponse>(`/auth/callback?${q.toString()}`);
      return res.data;
    },

    logout: async (): Promise<{ status: string }> => {
      const res = await this.request<{ status: string }>("/auth/logout", { method: "POST" });
      return res.data;
    },

    csrf: async (): Promise<CsrfResponse> => {
      const res = await this.request<CsrfResponse>("/auth/csrf");
      if (res.data.csrf_token) {
        this.setCsrfToken(res.data.csrf_token);
      }
      return res.data;
    },

    me: async (): Promise<UserProfileResponse> => {
      const res = await this.request<UserProfileResponse>("/me");
      return res.data;
    },
  };

  // -------------------------------------------------------------------------
  // Uploads Namespaces (§7 & SAAS-307)
  // -------------------------------------------------------------------------

  public readonly uploads = {
    initiate: async (req: UploadCreateRequest): Promise<UploadResponse> => {
      const res = await this.request<UploadResponse>("/uploads", {
        method: "POST",
        body: req,
      });
      return res.data;
    },

    uploadContent: async (
      uploadId: string,
      data: Blob | ArrayBuffer | Uint8Array,
      mimeType: string = "application/octet-stream"
    ): Promise<{ status: string; byte_size: number; sha256: string }> => {
      const res = await this.request<{ status: string; byte_size: number; sha256: string }>(
        `/uploads/${uploadId}/content`,
        {
          method: "PUT",
          headers: { "Content-Type": mimeType },
          body: data,
        }
      );
      return res.data;
    },

    complete: async (uploadId: string, idempotencyKey?: string): Promise<UploadCompleteResponse> => {
      const headers: Record<string, string> = {};
      if (idempotencyKey) {
        headers["Idempotency-Key"] = idempotencyKey;
      }
      const res = await this.request<UploadCompleteResponse>(`/uploads/${uploadId}/complete`, {
        method: "POST",
        headers,
      });
      return res.data;
    },

    get: async (uploadId: string): Promise<UploadResponse> => {
      const res = await this.request<UploadResponse>(`/uploads/${uploadId}`);
      return res.data;
    },
  };

  private async requestBinary(path: string): Promise<{ data: Blob; headers: Headers }> {
    const url = `${this.baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
    const headers: Record<string, string> = {
      Accept: "image/*,application/octet-stream",
      "X-Request-Id": generateRequestId(),
    };
    if (this.tenantId) headers["X-Tenant-Id"] = this.tenantId;
    const response = await fetch(url, { headers, credentials: "same-origin" });
    if (!response.ok) {
      throw new ApiClientError(
        `http_${response.status}`,
        `HTTP request failed with status ${response.status}`,
        response.status,
        response.headers.get("X-Request-Id") || headers["X-Request-Id"],
        response.status >= 500,
      );
    }
    return { data: await response.blob(), headers: response.headers };
  }

  public readonly assets = {
    getContent: async (assetId: string): Promise<Blob> => {
      const res = await this.requestBinary(`/assets/${encodeURIComponent(assetId)}/content`);
      return res.data;
    },
  };

  // -------------------------------------------------------------------------
  // Documents Namespaces (§7 & SAAS-308 & SAAS-309)
  // -------------------------------------------------------------------------

  public readonly documents = {
    create: async (req: DocumentCreateRequest = {}): Promise<DocumentResponse> => {
      const res = await this.request<DocumentResponse>("/documents", {
        method: "POST",
        body: req,
      });
      return res.data;
    },

    list: async (cursor?: string, limit: number = 20): Promise<DocumentListResponse> => {
      const q = new URLSearchParams();
      if (cursor) q.set("cursor", cursor);
      if (limit) q.set("limit", limit.toString());
      const queryStr = q.toString() ? `?${q.toString()}` : "";
      const res = await this.request<DocumentListResponse>(`/documents${queryStr}`);
      return res.data;
    },

    get: async (documentId: string): Promise<DocumentResponse> => {
      const res = await this.request<DocumentResponse>(`/documents/${documentId}`);
      return res.data;
    },

    update: async (
      documentId: string,
      req: DocumentUpdateRequest,
      ifMatchVersion?: number
    ): Promise<DocumentResponse> => {
      const ifMatch = ifMatchVersion !== undefined ? `W/"${ifMatchVersion}"` : undefined;
      const res = await this.request<DocumentResponse>(`/documents/${documentId}`, {
        method: "PATCH",
        body: req,
        ifMatch,
      });
      return res.data;
    },

    delete: async (
      documentId: string
    ): Promise<{ document_id: string; state: string; deletion_request_id: string; status: string }> => {
      const res = await this.request<{
        document_id: string;
        state: string;
        deletion_request_id: string;
        status: string;
      }>(`/documents/${documentId}`, {
        method: "DELETE",
      });
      return res.data;
    },

    deleteSource: async (documentId: string): Promise<{ status: string; document_id: string }> => {
      const res = await this.request<{ status: string; document_id: string }>(`/documents/${documentId}/source`, {
        method: "DELETE",
      });
      return res.data;
    },

    createRevision: async (
      documentId: string,
      req: RevisionCreateRequest,
      ifMatchRevisionId?: string
    ): Promise<RevisionResponse> => {
      const ifMatch = ifMatchRevisionId ? `W/"${ifMatchRevisionId}"` : undefined;
      const res = await this.request<RevisionResponse>(`/documents/${documentId}/revisions`, {
        method: "POST",
        body: req,
        ifMatch,
      });
      return res.data;
    },

    listRevisions: async (documentId: string, limit: number = 50): Promise<RevisionListResponse> => {
      const q = new URLSearchParams({ limit: limit.toString() });
      const res = await this.request<RevisionListResponse>(`/documents/${documentId}/revisions?${q.toString()}`);
      return res.data;
    },

    getRevision: async (documentId: string, revisionId: string): Promise<RevisionResponse> => {
      const res = await this.request<RevisionResponse>(`/documents/${documentId}/revisions/${revisionId}`);
      return res.data;
    },

    createBranch: async (documentId: string, req: BranchCreateRequest): Promise<BranchResponse> => {
      const res = await this.request<BranchResponse>(`/documents/${documentId}/branches`, {
        method: "POST",
        body: req,
      });
      return res.data;
    },

    listBranches: async (documentId: string, includeArchived: boolean = false): Promise<BranchListResponse> => {
      const q = new URLSearchParams({ include_archived: includeArchived.toString() });
      const res = await this.request<BranchListResponse>(`/documents/${documentId}/branches?${q.toString()}`);
      return res.data;
    },

    getBranch: async (documentId: string, branchId: string): Promise<BranchResponse> => {
      const res = await this.request<BranchResponse>(`/documents/${documentId}/branches/${branchId}`);
      return res.data;
    },

    archiveBranch: async (documentId: string, branchId: string): Promise<BranchResponse> => {
      const res = await this.request<BranchResponse>(`/documents/${documentId}/branches/${branchId}`, {
        method: "DELETE",
      });
      return res.data;
    },

    setHead: async (
      documentId: string,
      req: HeadUpdateRequest,
      ifMatchRevisionId?: string
    ): Promise<DocumentResponse> => {
      const ifMatch = ifMatchRevisionId ? `W/"${ifMatchRevisionId}"` : undefined;
      const res = await this.request<DocumentResponse>(`/documents/${documentId}/head`, {
        method: "POST",
        body: req,
        ifMatch,
      });
      return res.data;
    },

    createConversion: async (
      documentId: string,
      req: ConversionCreateRequest,
      idempotencyKey?: string
    ): Promise<ConversionResponse> => {
      const headers: Record<string, string> = {};
      if (idempotencyKey) {
        headers["Idempotency-Key"] = idempotencyKey;
      }
      const res = await this.request<ConversionResponse>(`/documents/${documentId}/conversions`, {
        method: "POST",
        body: req,
        headers,
      });
      return res.data;
    },

    listConversions: async (documentId: string): Promise<ConversionListResponse> => {
      const res = await this.request<ConversionListResponse>(`/documents/${documentId}/conversions`);
      return res.data;
    },

    getConversion: async (documentId: string, conversionId: string): Promise<ConversionResponse> => {
      const res = await this.request<ConversionResponse>(`/documents/${documentId}/conversions/${conversionId}`);
      return res.data;
    },
  };

  public readonly notebooks = {
    getDefault: async (): Promise<NotebookResponse> => {
      const res = await this.request<NotebookResponse>(`/notebooks/default`);
      return res.data;
    },

    listPages: async (
      notebookId: string,
      cursor?: string,
      limit: number = 20
    ): Promise<PageListResponse> => {
      const q = new URLSearchParams();
      if (cursor) q.set("cursor", cursor);
      q.set("limit", limit.toString());
      const res = await this.request<PageListResponse>(
        `/notebooks/${notebookId}/pages?${q.toString()}`
      );
      return res.data;
    },

    createPage: async (
      notebookId: string,
      req: PageCreateRequest,
      idempotencyKey: string
    ): Promise<PageCreateResponse> => {
      const res = await this.request<PageCreateResponse>(`/notebooks/${notebookId}/pages`, {
        method: "POST",
        body: req,
        idempotencyKey,
      });
      return res.data;
    },

    initializeLegacyPage: async (
      notebookId: string,
      documentId: string,
      idempotencyKey: string
    ): Promise<PageCreateResponse> => {
      const res = await this.request<PageCreateResponse>(
        `/notebooks/${notebookId}/pages/${documentId}/initialize`,
        { method: "POST", body: {}, idempotencyKey }
      );
      return res.data;
    },

    getResume: async (notebookId: string, deviceId: string): Promise<ResumeResponse> => {
      const res = await this.request<ResumeResponse>(
        `/notebooks/${notebookId}/resume?device_id=${encodeURIComponent(deviceId)}`
      );
      return res.data;
    },

    putResume: async (notebookId: string, req: ResumePutRequest): Promise<ResumeResponse> => {
      const res = await this.request<ResumeResponse>(`/notebooks/${notebookId}/resume`, {
        method: "PUT",
        body: req,
      });
      return res.data;
    },
  };

  public readonly jobs = {
    get: async (jobId: string): Promise<JobResponse> => {
      const res = await this.request<JobResponse>(`/jobs/${jobId}`);
      return res.data;
    },

    cancel: async (jobId: string): Promise<JobResponse> => {
      // The cancel route enforces the Idempotency-Key header (Header(...) is
      // required server-side). Without it the request died with 422 before it
      // ever reached the job — the cancel button looked dead.
      const res = await this.request<JobResponse>(`/jobs/${jobId}/cancel`, {
        method: "POST",
        idempotencyKey: `idem_cancel_${jobId.substring(0, 8)}_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      });
      return res.data;
    },
  };
}
